// Single source of truth for the report sales pages' URLs and route titles.
// Deliberately dependency-free (same reasoning as
// `sales-forecast.routes-data.ts`): imported by the root router, the server
// route table, the report catalogue, and scripts/sitemap-reports.ts via tsx -
// none of which should pull the page copy or the order flow in with it.
//
// `reportId` is the backend's `ReportType` string, the same value as the
// `ReportTypes` enum key; spelled as a literal here so this file imports
// nothing.
//
// `label` is the short name for links and breadcrumbs (the footer, the page's
// own trail); the full product name stays `ReportData.title`.

export const REPORT_PAGES_BASE_PATH = 'reports';

export const REPORT_PAGES = [
  {
    slug: 'community-compass',
    label: 'Community compass',
    reportId: 'COMMUNITY_COMPASS',
    title: 'Neighborhood Report for Any US Address | Map AI',
  },
  {
    slug: 'open-house-flyer',
    label: 'Open house flyer',
    reportId: 'OPEN_HOUSE_FLYER',
    title: 'Open House Flyer with Neighborhood Stats | Map AI',
  },
  {
    slug: 'business-site-traffic',
    label: 'Business Site Traffic',
    reportId: 'LOCATION_INTELLIGENCE',
    title: 'Traffic Count Report for a Retail Address | Map AI',
  },
  {
    slug: 'feasibility-study',
    label: 'Feasibility study',
    reportId: 'FEASIBILITY_STUDY',
    title: 'Bankable Feasibility Study for One Address | Map AI',
  },
  {
    slug: 'comparative-location-analysis',
    label: 'Comparative location analysis',
    reportId: 'COMPARATIVE_LOCATION',
    title: 'Comparative Location Analysis Report | Map AI',
  },
] as const;

export type ReportPageRoute = (typeof REPORT_PAGES)[number];
export type ReportPageSlug = ReportPageRoute['slug'];

/** Route data key holding the slug for `ReportPageComponent`. */
export const REPORT_PAGE_SLUG_KEY = 'reportPageSlug';

export function reportPagePath(slug: ReportPageSlug): string {
  return `${REPORT_PAGES_BASE_PATH}/${slug}`;
}

/**
 * The sales page for a report id, or undefined for a report that has none
 * (the internal feasibility study). Absolute, for `routerLink` and hrefs.
 */
export function reportPageUrl(reportId: string): string | undefined {
  const page = REPORT_PAGES.find((p) => p.reportId === reportId);
  return page ? `/${reportPagePath(page.slug)}` : undefined;
}
